/**
 * TemplateEditor — reusable modal for creating + editing MarketingTemplate.
 *
 * Used by:
 *   - TemplateLibrary "+ New Template" and "Edit" actions
 *   - Drip Builder per-step edit (with category + trigger locked)
 *
 * Includes a token palette that inserts tokens at the textarea cursor and a
 * debounced live preview pane that calls the existing render endpoints.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2, Plus, Trash2, Eye } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api.js';

const VERTICAL_OPTIONS = [
  { value: 'veteran', label: 'Veteran' },
  { value: 'iul', label: 'IUL / Tax-Free Retirement' },
  { value: 'final_expense', label: 'Final Expense' },
  { value: 'mortgage_protection', label: 'Mortgage Protection' },
  { value: 'generic', label: 'Generic' },
  { value: 'all', label: 'Custom / All' },
];

const CATEGORY_OPTIONS = [
  { value: 'preset', label: 'Pre-Set' },
  { value: 'drip', label: 'Drip' },
  { value: 'disposition', label: 'Disposition' },
  { value: 'auto_reply', label: 'Auto-Reply' },
];

// trigger shapes per spec section 1. `kind` drives whether a value input appears.
const TRIGGER_KINDS = [
  { kind: 'new_lead', label: 'New Lead' },
  { kind: 'disposition_change', label: 'Disposition Change', needsValue: true, valueLabel: 'Disposition value' },
  { kind: 'keyword', label: 'Keyword', needsValue: true, valueLabel: 'Keyword' },
  { kind: 'manual', label: 'Manual' },
  { kind: 'birthday', label: 'Birthday' },
];

function parseTrigger(trigger) {
  const s = String(trigger || '').trim();
  if (!s) return { kind: 'new_lead', value: '' };
  for (const k of TRIGGER_KINDS) {
    if (k.needsValue && s.startsWith(`${k.kind}:`)) {
      return { kind: k.kind, value: s.slice(k.kind.length + 1) };
    }
    if (!k.needsValue && s === k.kind) return { kind: k.kind, value: '' };
  }
  return { kind: 'manual', value: '' };
}

function composeTrigger(kind, value) {
  const meta = TRIGGER_KINDS.find((k) => k.kind === kind);
  if (meta?.needsValue) return `${kind}:${String(value || '').trim()}`;
  return kind;
}

const DEFAULT_TOKENS = [
  'first_name', 'last_name', 'full_name', 'my_first_name', 'my_last_name',
  'my_full_name', 'my_company_name', 'my_office_phone_number', 'my_email',
  'my_booking_link', 'my_business_website', 'calendar_link', 'agent_first_name',
  'client_first_name', 'state', 'city', 'zip', 'birthday', 'dob', 'vertical',
  'lead_address', 'opt_out_line', 'new_line', 'last_carrier_quoted',
  'quoted_monthly', 'booked_appointment_time',
];

export default function TemplateEditor({
  template = null,
  defaults = {},
  lockedFields = {},
  editOnly = false,
  onClose,
  onSaved,
}) {
  const isEdit = !!template?._id;
  const [name, setName] = useState(template?.name || defaults.name || '');
  const [vertical, setVertical] = useState(template?.vertical || defaults.vertical || 'generic');
  const [category, setCategory] = useState(template?.category || defaults.category || 'preset');
  const init = parseTrigger(template?.trigger ?? defaults.trigger ?? 'manual');
  const [triggerKind, setTriggerKind] = useState(init.kind);
  const [triggerValue, setTriggerValue] = useState(init.value);
  const [scheduleOffsetHours, setScheduleOffsetHours] = useState(
    Number(template?.scheduleOffsetHours ?? defaults.scheduleOffsetHours ?? 0)
  );
  const [scheduleTimeOfDay, setScheduleTimeOfDay] = useState(
    template?.scheduleTimeOfDay || defaults.scheduleTimeOfDay || ''
  );
  const [body, setBody] = useState(template?.body || defaults.body || '');
  const [rotatingVariants, setRotatingVariants] = useState(
    Array.isArray(template?.rotatingVariants) ? template.rotatingVariants
      : (Array.isArray(defaults.rotatingVariants) ? defaults.rotatingVariants : [])
  );
  const [silent, setSilent] = useState(!!(template?.silent ?? defaults.silent));
  const [saving, setSaving] = useState(false);

  const [tokens, setTokens] = useState(DEFAULT_TOKENS);
  useEffect(() => {
    apiGet('/marketing/templates/tokens')
      .then((r) => { if (Array.isArray(r?.tokens) && r.tokens.length) setTokens(r.tokens); })
      .catch(() => {});
  }, []);

  // Cursor-aware insertion into whichever textarea is "focused".
  const bodyRef = useRef(null);
  const variantRefs = useRef([]);
  const activeRef = useRef({ kind: 'body', index: -1 });

  function setActive(kind, index = -1) {
    activeRef.current = { kind, index };
  }

  function insertToken(token) {
    const wrapped = `#${token}`;
    const active = activeRef.current;
    if (active.kind === 'variant' && active.index >= 0) {
      const el = variantRefs.current[active.index];
      const next = insertAtCursor(el, rotatingVariants[active.index] || '', wrapped);
      const copy = [...rotatingVariants];
      copy[active.index] = next.value;
      setRotatingVariants(copy);
      requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(next.pos, next.pos); } catch (_) {} });
      return;
    }
    const el = bodyRef.current;
    const next = insertAtCursor(el, body, wrapped);
    setBody(next.value);
    requestAnimationFrame(() => { try { el?.focus(); el?.setSelectionRange(next.pos, next.pos); } catch (_) {} });
  }

  // Live preview — debounced render via the existing service. We mint a
  // throwaway template by POSTing to /tokens-render-preview-equivalent.
  // Since the route requires an existing template ID, we render locally:
  // a simple client-side substitution is fine for the preview UX. (The real
  // render runs at send time.)
  const [previewBody, setPreviewBody] = useState('');
  const [previewVariants, setPreviewVariants] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      setPreviewBody(localRender(body));
      setPreviewVariants(rotatingVariants.map(localRender));
    }, 200);
    return () => clearTimeout(t);
  }, [body, rotatingVariants]);

  const triggerMeta = TRIGGER_KINDS.find((k) => k.kind === triggerKind);

  async function handleSave() {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!silent && !body.trim()) { toast.error('Body is required unless silent'); return; }
    if (triggerMeta?.needsValue && !triggerValue.trim()) {
      toast.error(`${triggerMeta.valueLabel} is required`);
      return;
    }
    const payload = {
      name: name.trim(),
      vertical,
      category,
      trigger: composeTrigger(triggerKind, triggerValue),
      scheduleOffsetHours: Number(scheduleOffsetHours) || 0,
      scheduleTimeOfDay,
      body,
      rotatingVariants: rotatingVariants.filter((v) => v && v.trim()),
      silent,
    };

    // editOnly mode: the parent (e.g. DripBuilder) owns persistence.
    // Return the form state directly without hitting the API.
    if (editOnly) {
      onSaved?.(payload);
      return;
    }

    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const r = await apiPut(`/marketing/templates/${template._id}`, payload);
        saved = r?.template;
      } else {
        const r = await apiPost('/marketing/templates', payload);
        saved = r?.template;
      }
      toast.success(isEdit ? 'Template updated' : 'Template created');
      onSaved?.(saved);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const locked = lockedFields || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="panel p-5 max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="template-editor"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="text-white font-bold text-lg">{isEdit || editOnly ? 'Edit Template' : 'New Template'}</div>
          <button onClick={onClose} className="text-white/50 hover:text-white" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <Field label="Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="te-name"
                className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vertical">
                <select
                  value={vertical}
                  onChange={(e) => setVertical(e.target.value)}
                  className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  {VERTICAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={!!locked.category}
                  data-testid="te-category"
                  className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60"
                >
                  {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {locked.category && <div className="text-[10px] text-white/40 mt-0.5">Locked (drip step)</div>}
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Trigger">
                <select
                  value={triggerKind}
                  onChange={(e) => setTriggerKind(e.target.value)}
                  disabled={!!locked.trigger}
                  data-testid="te-trigger"
                  className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60"
                >
                  {TRIGGER_KINDS.map((t) => <option key={t.kind} value={t.kind}>{t.label}</option>)}
                </select>
                {locked.trigger && <div className="text-[10px] text-white/40 mt-0.5">Locked (drip step)</div>}
              </Field>
              {triggerMeta?.needsValue && (
                <Field label={triggerMeta.valueLabel}>
                  <input
                    value={triggerValue}
                    onChange={(e) => setTriggerValue(e.target.value)}
                    disabled={!!locked.trigger}
                    className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60"
                  />
                </Field>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Send X hours after trigger fires">
                <input
                  type="number"
                  min="0"
                  value={scheduleOffsetHours}
                  onChange={(e) => setScheduleOffsetHours(e.target.value)}
                  className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </Field>
              <Field label="Pinned time of day (HH:MM, optional)">
                <input
                  value={scheduleTimeOfDay}
                  onChange={(e) => setScheduleTimeOfDay(e.target.value)}
                  placeholder="e.g. 10:00"
                  className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </Field>
            </div>
            <Field label="Body">
              <textarea
                ref={bodyRef}
                value={body}
                onFocus={() => setActive('body')}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                data-testid="te-body"
                className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono"
              />
              <div className="text-[11px] text-white/40 mt-1">{body.length}/1600 chars · tap a token below to insert</div>
            </Field>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-wide text-white/40">Rotating variants ({rotatingVariants.length})</label>
                <button
                  type="button"
                  onClick={() => setRotatingVariants([...rotatingVariants, ''])}
                  className="text-xs px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-white/70 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add variant
                </button>
              </div>
              <div className="space-y-2">
                {rotatingVariants.map((v, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <textarea
                      ref={(el) => { variantRefs.current[i] = el; }}
                      value={v}
                      onFocus={() => setActive('variant', i)}
                      onChange={(e) => {
                        const copy = [...rotatingVariants]; copy[i] = e.target.value; setRotatingVariants(copy);
                      }}
                      rows={2}
                      className="flex-1 bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setRotatingVariants(rotatingVariants.filter((_, j) => j !== i))}
                      className="p-2 text-red-300 hover:bg-red-500/15 rounded-sm"
                      aria-label="Remove variant"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-white/70 mt-2">
              <input type="checkbox" checked={silent} onChange={(e) => setSilent(e.target.checked)} />
              Silent — pause this template (won't send)
            </label>
          </div>

          <aside className="space-y-3">
            <div className="panel p-3 bg-civix-panelLight/40">
              <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Token palette</div>
              <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
                {tokens.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => insertToken(t)}
                    className="text-[11px] px-2 py-1 rounded-sm bg-civix-purple/15 hover:bg-civix-purple/30 text-civix-purple font-mono"
                  >
                    #{t}
                  </button>
                ))}
              </div>
            </div>

            <div className="panel p-3 bg-civix-panelLight/40">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-white/40 mb-2">
                <Eye className="w-3 h-3" /> Live preview
              </div>
              <pre className="text-sm text-white whitespace-pre-wrap bg-linear-to-br from-civix-purple/10 to-cyan-500/10 border border-civix-purple/20 rounded-lg p-3 min-h-[80px]">
                {previewBody || <span className="text-white/30 italic">empty</span>}
              </pre>
              {previewVariants.length > 0 && (
                <div className="mt-2 space-y-2">
                  {previewVariants.map((p, i) => (
                    <pre key={i} className="text-xs text-white/80 whitespace-pre-wrap bg-civix-panelLight border border-white/10 rounded-sm p-2">
                      {p}
                    </pre>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-white/10">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="te-save"
            className="btn-primary flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit || editOnly ? 'Save' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-white/40 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function insertAtCursor(el, current, insert) {
  if (!el) return { value: (current || '') + insert, pos: (current || '').length + insert.length };
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const value = current.slice(0, start) + insert + current.slice(end);
  return { value, pos: start + insert.length };
}

const LOCAL_PREVIEW_SAMPLE = {
  // ── Lead
  first_name: 'John',
  last_name: 'Doe',
  full_name: 'John Doe',
  email_address: 'john.doe@example.com',
  street_address: '123 Main St, Austin TX',
  city: 'Austin',
  state: 'TX',
  zip_code: '78701',
  birthday: 'April 12',
  next_month_full_name: 'August',
  vertical: 'iul',
  lead_age: '44',
  // ── Agent
  my_first_name: 'Sarah',
  my_last_name: 'Bishop',
  my_full_name: 'Sarah Bishop',
  my_email: 'sarah@civix.com',
  my_office_phone_number: '(555) 123-4567',
  my_forwarding_number: '(555) 987-6543',
  my_company_name: 'CIVIX Insurance',
  my_booking_link: 'civix.com/book/sarah',
  my_business_website: 'civix.com',
  my_producer_link: 'civix.com/producer/sarah',
  my_vcard_link: 'civixcrm.ai/c/sarah',
  digital_business_card_link: 'civixcrm.ai/c/sarah',
  // ── CIVIX-specific
  appointment_time: '7/15/25, 10:00 AM',
  carrier: 'Ethos',
  quoted_amount: '$42.50',
  // ── Format
  new_line: '\n',
  // ── Aliases (legacy palette names — resolve identically to their canonical server equivalents)
  client_first_name: 'John',               // alias: first_name
  agent_first_name: 'Sarah',              // alias: my_first_name
  calendar_link: 'civix.com/book/sarah',  // alias: my_booking_link
  zip: '78701',                           // alias: zip_code
  dob: 'April 12',                        // alias: birthday (server formats as Month Day)
  lead_address: '123 Main St, Austin TX', // alias: street_address
  last_carrier_quoted: 'Ethos',           // alias: carrier
  quoted_monthly: '$42.50',               // alias: quoted_amount
  booked_appointment_time: '7/15/25, 10:00 AM', // alias: appointment_time
  opt_out_line: 'Reply STOP to opt out',  // static compliance string
};

function localRender(template) {
  if (!template) return '';
  return String(template).replace(/#([a-zA-Z_][a-zA-Z0-9_]*)/g, (m, name) => {
    const k = name.toLowerCase();
    if (k in LOCAL_PREVIEW_SAMPLE) return LOCAL_PREVIEW_SAMPLE[k];
    return m;
  });
}
